// =============================================================================
// Offline mutation queue (IndexedDB)
// =============================================================================
// Catches network failures on writes and persists them to IndexedDB. Replays
// on reconnect or app boot, in FIFO order, with an `Idempotency-Key` header so
// the backend dedups any duplicate replays.
//
// Reads (GET) are NOT queued — TanStack Query handles those with its own
// retry + cache + the PWA service worker.

import { get, set, keys, del, createStore } from 'idb-keyval';
import type { AxiosRequestConfig } from 'axios';

const store = createStore('kalachand-offline', 'queue');

export interface QueuedRequest {
  id: string;            // idempotency key
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
  enqueuedAt: number;
  attempts: number;
}

const WRITE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

const listeners = new Set<(size: number) => void>();
function notify(): void {
  void size().then((n) => listeners.forEach((l) => l(n)));
}

export function isQueueable(config: AxiosRequestConfig): boolean {
  const m = (config.method ?? 'get').toUpperCase();
  return WRITE_METHODS.has(m);
}

export async function enqueue(req: Omit<QueuedRequest, 'enqueuedAt' | 'attempts'>): Promise<void> {
  const entry: QueuedRequest = { ...req, enqueuedAt: Date.now(), attempts: 0 };
  await set(req.id, entry, store);
  notify();
}

export async function size(): Promise<number> {
  return (await keys(store)).length;
}

export async function list(): Promise<QueuedRequest[]> {
  const ks = await keys(store);
  const items = await Promise.all(ks.map((k) => get<QueuedRequest>(k as string, store)));
  return items.filter((x): x is QueuedRequest => !!x).sort((a, b) => a.enqueuedAt - b.enqueuedAt);
}

export async function clear(): Promise<void> {
  const ks = await keys(store);
  await Promise.all(ks.map((k) => del(k as string, store)));
  notify();
}

let flushing = false;
export async function flush(replay: (req: QueuedRequest) => Promise<void>): Promise<{ ok: number; failed: number }> {
  if (flushing) return { ok: 0, failed: 0 };
  flushing = true;
  try {
    const items = await list();
    let ok = 0;
    let failed = 0;
    for (const item of items) {
      try {
        await replay(item);
        await del(item.id, store);
        ok++;
        notify();
      } catch (err) {
        failed++;
        // Mark attempt count; stop trying after 5 failures to avoid loops.
        item.attempts += 1;
        if (item.attempts >= 5) {
          await del(item.id, store);
          console.warn('[offlineQueue] dropping request after 5 failures', item.id, err);
        } else {
          await set(item.id, item, store);
        }
        notify();
      }
    }
    return { ok, failed };
  } finally {
    flushing = false;
  }
}

export function onQueueSizeChange(listener: (size: number) => void): () => void {
  listeners.add(listener);
  void size().then(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isNetworkError(err: unknown): boolean {
  if (typeof err !== 'object' || !err) return false;
  const e = err as { code?: string; message?: string; response?: unknown };
  if (e.response) return false; // server replied — not a network error
  return e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || /Network Error/i.test(e.message ?? '');
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
